import {
	error,
	rem,
	StylovyzeDialog,
	StylovyzeTable,
	success,
	useIsFeatureEnabled,
} from '@innovyze/stylovyze';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import {
	FormControlLabel,
	Grid,
	Radio,
	RadioGroup,
	TextField,
	Checkbox,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { PercentSlider } from '@Components';
import { RiskConfig } from '@Types';
import {
	setRiskSetupDirty,
	setSaveRun,
	saveRiskConfigAction,
	setRiskType,
	setRefreshList,
	setDidMatrixUpdate,
	setMatrixPreviewStatus,
	initRiskMatrixPreview,
	setOnlyGradesUpdated,
	clearMatrixUpdate,
	setOverallRisk,
} from '@Actions';
import {
	selectDidMatrixUpdate,
	selectEnablePreview,
	selectIsPreviewOnLoad,
	selectMaxCoFValue,
	selectMaxLoFValue,
	selectOnlyGradesUpdated,
	selectPreviewStatus,
	selectRiskConfig,
	selectRiskSetting,
	selectRiskStore,
	selectRiskType,
	selectSaveRunSettings,
	selectUpdatedRiskMatrixGrid,
} from '@Selectors';
import { useGlobalization } from '@Translations/useGlobalization';
import { RiskMatrixGrid, RiskModeDropdown } from './RiskMatrixDataGrid';
import { legends } from './utils.RiskMatrix';
import StarIcon from '@mui/icons-material/Star';
import { RiskNormalize } from './RiskNormalize.component';
import { RiskPostProcessing } from '@innovyze/lib_am_common';

export interface StylovyzeHeaderTemp {
	key: string;
	cell: ReactNode;
	width?: string;
	align?: 'center' | 'inherit' | 'justify' | 'left' | 'right';
	sortable?: boolean;
	maxWidth?: string;
	padding?: string;
}

interface RiskGradeProps {
	isLoading: boolean;
}

export const RiskGrade = ({ isLoading }: RiskGradeProps) => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();

	const riskMatrixFlag = useIsFeatureEnabled('info-360-risk-setup-matrix');
	const riskNormalizeFlag = useIsFeatureEnabled(
		'info-360-asset-risk-normalize',
	);

	const overallRows = [
		[t('LoF'), '', '10'],
		[t('CoF'), '', '10'],
	];

	const gradeRows: string[][] = [
		['negligible', t('Negligible'), '<', ''],
		['low', t('Low'), '>=', ''],
		['medium', t('Medium'), '>=', ''],
		['high', t('High'), '>=', ''],
		['extreme', t('Extreme'), '>=', ''],
	];

	const settings = selectRiskSetting();
	const riskConfig = selectRiskConfig();
	const saveRunSettings = selectSaveRunSettings();
	const riskType = selectRiskType();
	const previewStatus = selectPreviewStatus();
	const previewOnLoad = selectIsPreviewOnLoad();
	const enablePreview = selectEnablePreview();
	const isOnlyGradesUpdated = selectOnlyGradesUpdated();
	const maxLoFValue = selectMaxLoFValue();
	const maxCoFValue = selectMaxCoFValue();

	const [ignoreWeightings, setIgnoreWeightings] = useState<boolean>(
		settings.LOFWeighting === 100 && settings.COFWeighting === 100,
	);
	const [ignorePreviousValue, setIgnorePreviousValue] = useState<number>(
		settings.LOFWeighting === 100 && settings.COFWeighting === 100
			? 50
			: settings.LOFWeighting,
	);

	const [riskMethod, setRiskMethod] = useState<'added' | 'multiplied'>(
		settings.RiskMethod,
	);
	const [showMethodSwitchDialog, setShowMethodSwitchDialog] =
		useState<boolean>(false);

	const [lofValue, setLoFValue] = useState<number>(settings.LOFWeighting);
	const [lofScore, setLofScore] = useState<number>(maxLoFValue);
	const [cofValue, setCoFValue] = useState<number>(settings.COFWeighting);
	const [cofScore, setCofScore] = useState<number>(maxCoFValue);

	// Before anyone starts I have tried this using an array but it just wont work, the controls just dont accept any input and stay as the value in the array.
	const [gradeLow, setGradeLow] = useState<number>(settings.Grades.Low);
	const [gradeMedium, setGradeMedium] = useState<number>(
		settings.Grades.Medium,
	);
	const [gradeHigh, setGradeHigh] = useState<number>(settings.Grades.High);
	const [gradeExtreme, setGradeExtreme] = useState<number>(
		settings.Grades.Extreme,
	);
	const [gradeMax, setGradeMax] = useState<number>(settings.Grades.Max);

	// For older configs maxOverallRiskScore will be undefined so get it from the grades
	const [maxScore, setMaxScore] = useState<number>(
		settings.maxOverallRiskScore ?? settings.Grades.Max,
	);
	const [normalizeSettings, setNormalizeSettings] = useState<{
		useNormalize: boolean;
		normalizeValue: number;
		maxRiskScore?: number;
	}>({ useNormalize: false, normalizeValue: 100 });

	const setIsSetupDirty = (state: boolean) => {
		dispatch(setRiskSetupDirty(state));
	};

	const grid = selectUpdatedRiskMatrixGrid();

	useEffect(() => {
		setIsSetupDirty(false);
	}, []);

	useEffect(() => {
		setRiskMethod(settings.RiskMethod);
		setLoFValue(settings.LOFWeighting);
		setLofScore((settings.LOFWeighting / 100) * maxLoFValue);
		setCoFValue(settings.COFWeighting);
		setCofScore((settings.COFWeighting / 100) * maxCoFValue);
		setGradeLow(settings.Grades.Low);
		setGradeMedium(settings.Grades.Medium);
		setGradeHigh(settings.Grades.High);
		setGradeExtreme(settings.Grades.Extreme);
		setGradeMax(settings.Grades.Max);

		setMaxScore(settings.maxOverallRiskScore ?? settings.Grades.Max);

		setNormalizeSettings({ useNormalize: false, normalizeValue: 100 });

		if (settings.postProcessing) {
			const process = settings.postProcessing.find(
				process => process.type === 'normalize',
			);
			if (process) {
				setNormalizeSettings({
					useNormalize: process.params.useNormalize ?? false,
					normalizeValue: process.params.normalizeValue ?? 100,
					maxRiskScore: process.params.maxRiskScore,
				});
			}
		}

		if (
			riskMatrixFlag &&
			riskType === 'matrix' &&
			settings.matrix &&
			!previewOnLoad &&
			previewStatus === ''
		) {
			const previewObjPyld = {
				cofWeighting: settings.COFWeighting,
				lofWeighting: settings.LOFWeighting,
				riskMethod: settings.RiskMethod,
				grades: settings.matrix.grades,
				grid,
				preview: {
					grades: settings.matrix.grades,
					grid,
				},
				configId: settings.configId,
				initialLoad: true,
			};
			dispatch(initRiskMatrixPreview(previewObjPyld));
		}
	}, [settings]);

	useEffect(() => {
		const lScore = getScore(lofValue, true);
		const cScore = getScore(cofValue, false);
		const max =
			Math.round(
				(riskMethod === 'multiplied'
					? lScore * cScore
					: lScore + cScore) * 10,
			) / 10;

		if (maxScore !== max) {
			maxScoreChanged(riskMethod, lScore, lofValue, cScore, cofValue);
			dispatch(success(t('Max Overall Risk Score Updated')));
		}
	}, [settings, maxLoFValue, maxCoFValue]);

	useEffect(() => {
		if (saveRunSettings) {
			if (gradeMax != 0) {
				const newSettings = { ...settings };
				newSettings.RiskMethod = riskMethod;
				newSettings.LOFWeighting = lofValue;
				newSettings.COFWeighting = cofValue;
				newSettings.Grades = {
					Low: gradeLow,
					Medium: gradeMedium,
					High: gradeHigh,
					Extreme: gradeExtreme,
					Max: gradeMax,
				};
				newSettings.maxOverallRiskScore = maxScore;

				if (riskNormalizeFlag) {
					const params: RiskPostProcessing[] = [
						{
							type: 'normalize',
							params: {
								useNormalize: normalizeSettings.useNormalize,
								normalizeValue:
									normalizeSettings.normalizeValue,
								maxRiskScore: normalizeSettings.maxRiskScore,
							},
						},
					];
					newSettings.postProcessing = params;
				}

				const newMatrix = {
					grades: { ...riskStore.riskMatrix.grades },
					grid: [...riskStore.riskMatrix.grid],
				};
				newSettings.matrix = newMatrix;

				const newRiskConfig: RiskConfig = {
					...riskConfig,
					riskSetting: newSettings,
				};
				dispatch(saveRiskConfigAction(newRiskConfig));

				if (enablePreview && !isOnlyGradesUpdated)
					dispatch(clearMatrixUpdate(false));
			} else {
				dispatch(error(t('Invalid overall risk weighting')));
			}
			dispatch(setSaveRun(false));
		}
	}, [saveRunSettings]);

	const createRangeValues = (
		start: number,
		end: number,
		precision: number,
		count: number,
	) => {
		const result: number[] = [count + 1];
		const range = end - start;
		let interval = range / count;

		if (precision === 0) interval = Math.floor(interval);
		else
			interval =
				Math.round(interval * (100 * precision)) / (100 * precision);

		let first = start;
		let pos = 0;

		result[pos++] = first;

		for (let i = 0; i < count - 1; i++) {
			result[pos++] =
				Math.round((first += interval) * (100 * precision)) /
				(100 * precision);
		}

		result[pos++] = end;

		return result;
	};

	const getScore = (value: number, lof: boolean) => {
		const result = (value / 100) * (lof ? maxLoFValue : maxCoFValue);
		return Math.round(result * 10) / 10;
	};

	const maxScoreChanged = (
		method: string,
		lof: number,
		lofWeighting: number,
		cof: number,
		cofWeighting: number,
	) => {
		const max =
			Math.round((method === 'multiplied' ? lof * cof : lof + cof) * 10) /
			10;

		setMaxScore(max);

		if (!normalizeSettings.useNormalize) {
			const range = createRangeValues(0, max, 1, 5);
			setGradeLow(range[1]);
			setGradeMedium(range[2]);
			setGradeHigh(range[3]);
			setGradeExtreme(range[4]);
			setGradeMax(max);
		}

		setRiskMethod(method === 'multiplied' ? 'multiplied' : 'added');
		setIsSetupDirty(true);
		dispatch(
			setOverallRisk({
				cofWeighting: cofWeighting,
				lofWeighting: lofWeighting,
				riskMethod: method === 'multiplied' ? 'multiplied' : 'added',
			}),
		);
	};

	const lofSliderChanged = (value: number) => {
		if (!ignoreWeightings) {
			const lof = getScore(value, true);
			setLoFValue(value);
			setLofScore(lof);

			const cof = getScore(100 - value, false);
			setCoFValue(100 - value);
			setCofScore(cof);

			maxScoreChanged(riskMethod, lof, value, cof, 100 - value);
		} else {
			setLoFValue(100);
			setLofScore(maxLoFValue);

			setCoFValue(100);
			setCofScore(maxCoFValue);

			maxScoreChanged(riskMethod, maxLoFValue, value, maxCoFValue, 100);
		}

		setIsSetupDirty(true);
	};

	const cofSliderChanged = (value: number) => {
		if (!ignoreWeightings) {
			const cof = getScore(value, false);
			setCoFValue(value);
			setCofScore(cof);

			const lof = getScore(100 - value, true);
			setLoFValue(100 - value);
			setLofScore(lof);

			maxScoreChanged(riskMethod, lof, 100 - value, cof, value);
		} else {
			setLoFValue(100);
			setLofScore(maxLoFValue);

			setCoFValue(100);
			setCofScore(maxCoFValue);

			maxScoreChanged(riskMethod, maxLoFValue, value, maxCoFValue, 100);
		}

		setIsSetupDirty(true);
	};

	const overallHeaders = useMemo((): StylovyzeHeaderTemp[] => {
		return [
			{
				key: 'risk',
				cell: t('Risk'),
				sortable: false,
				align: 'left',
				width: '50px',
			},
			{
				key: 'weighting',
				cell: t('Weighting'),
				sortable: false,
				align: 'left',
				padding: '0',
			},
			{
				key: 'maxScore',
				cell: t('Max Score'),
				sortable: false,
				align: 'left',
				width: '50px',
			},
		];
	}, [settings]);

	const overallRowReactNodes = (
		row: string[],
		nRow: number,
	): React.ReactNode[] => {
		return row.map((cell, nCol) => {
			switch (nCol) {
				case 1:
					return {
						content: (
							<PercentSlider
								disabled={isLoading || ignoreWeightings}
								dataCy={'overall-risk-' + nRow}
								value={nRow === 0 ? lofValue : cofValue}
								marks={'minmaxticks'}
								onSliderChanged={
									nRow === 0
										? lofSliderChanged
										: cofSliderChanged
								}
								padLeft={'15px'}
								valueLabelDisplay={'on'}
								invalid={maxScore == 0}
							/>
						),
						props: {
							padding: 'checkbox',
						},
					};
				case 2:
					return {
						content:
							nRow === 0
								? `${lofScore.toFixed(1)} / ${maxLoFValue}`
								: `${cofScore.toFixed(1)} / ${maxCoFValue}`,
						props: {
							size: 'small',
						},
					};
				default:
					return {
						content: cell,
						props: {
							size: 'small',
						},
					};
			}
		});
	};

	const gradeHeaders = useMemo((): StylovyzeHeaderTemp[] => {
		return [
			{
				key: 'status',
				cell: '',
				sortable: false,
				maxWidth: '30px',
				padding: '0 20px 0 10px',
			},
			{
				key: 'riskGrade',
				cell: t('Risk Grade'),
				sortable: false,
				padding: `0 ${rem(24)} 0 0`,
			},
			{
				key: 'indicator',
				cell: '',
				align: 'right',
				sortable: false,
				padding: '0',
			},
			{
				key: 'range',
				cell: t('Score Range'),
				sortable: false,
				width: '100px',
			},
		];
	}, [settings]);

	const onRangeChangedLow = (value: number) => {
		if (value < 0) value = 0;
		else if (value > maxScore) value = maxScore;

		// eslint-disable-next-line @typescript-eslint/no-use-before-define
		if (value > gradeMedium) onRangeChangedMedium(value);

		setGradeLow(value);
		setIsSetupDirty(true);
	};

	const onRangeChangedMedium = (value: number) => {
		if (value < 0) value = 0;
		else if (value > maxScore) value = maxScore;

		// eslint-disable-next-line @typescript-eslint/no-use-before-define
		if (value > gradeHigh) onRangeChangedHigh(value);
		else if (value < gradeLow) onRangeChangedLow(value);

		setGradeMedium(value);
		setIsSetupDirty(true);
	};

	const onRangeChangedHigh = (value: number) => {
		if (value < 0) value = 0;
		else if (value > maxScore) value = maxScore;

		// eslint-disable-next-line @typescript-eslint/no-use-before-define
		if (value > gradeExtreme) onRangeChangedExtreme(value);
		else if (value < gradeMedium) onRangeChangedMedium(value);

		setGradeHigh(value);
		setIsSetupDirty(true);
	};

	const onRangeChangedExtreme = (value: number) => {
		if (value < 0) value = 0;
		else if (value > gradeMax) value = gradeMax;

		if (value < gradeHigh) onRangeChangedHigh(value);

		setGradeExtreme(value);
		setIsSetupDirty(true);
	};

	const gradeRowReactNodes = (
		row: string[],
		nRow: number,
	): React.ReactNode[] => {
		return row.map((cell, nCol) => {
			switch (nCol) {
				case 0: {
					let cell;
					const legend = legends.find(item => item.name === row[0]);
					if (legend)
						cell = (
							<StarIcon
								style={{
									width: '16px',
									marginBottom: '2px',
									color: legend.color,
								}}
							/>
						);
					else cell = '';

					return {
						content: cell,
						props: {
							padding: 'checkbox',
							size: 'small',
						},
					};
				}
				case 3:
					{
						switch (nRow) {
							case 0:
								return isNaN(gradeLow) ? '' : gradeLow;
							case 1:
								return (
									<TextField
										disabled={isLoading}
										style={{ width: '100px' }}
										value={gradeLow}
										onChange={e => {
											let value = parseFloat(
												e.target.value,
											);
											if (!isNaN(value)) {
												if (value < 0) value = 0;
												else if (value > gradeMax)
													value = gradeMax;

												value =
													Math.round(value * 100) /
													100;
											}

											setGradeLow(value);
											setIsSetupDirty(true);
										}}
										onBlur={e => {
											let value = parseFloat(
												e.target.value,
											);
											if (isNaN(value)) value = 0;
											onRangeChangedLow(
												Math.round(value * 100) / 100,
											);
										}}
										inputProps={{
											step: '0.01',
											min: 0,
											max: gradeMax,
										}}
										variant="outlined"
										type="number"
										size="small"
									/>
								);
							case 2:
								return (
									<TextField
										disabled={isLoading}
										style={{ width: '100px' }}
										value={gradeMedium}
										onChange={e => {
											let value = parseFloat(
												e.target.value,
											);
											if (!isNaN(value)) {
												if (value < 0) value = 0;
												else if (value > gradeMax)
													value = gradeMax;

												value =
													Math.round(value * 100) /
													100;
											}

											setGradeMedium(value);
											setIsSetupDirty(true);
										}}
										onBlur={e => {
											let value = parseFloat(
												e.target.value,
											);
											if (isNaN(value)) value = gradeLow;

											onRangeChangedMedium(
												Math.round(value * 100) / 100,
											);
										}}
										inputProps={{
											step: '0.01',
											min: 0,
											max: gradeMax,
										}}
										variant="outlined"
										type="number"
										size="small"
									/>
								);
							case 3:
								return (
									<TextField
										disabled={isLoading}
										style={{ width: '100px' }}
										value={gradeHigh}
										onChange={e => {
											let value = parseFloat(
												e.target.value,
											);
											if (!isNaN(value)) {
												if (value < 0) value = 0;
												else if (value > gradeMax)
													value = gradeMax;

												value =
													Math.floor(value * 100) /
													100;
											}

											setGradeHigh(value);
											setIsSetupDirty(true);
										}}
										onBlur={e => {
											let value = parseFloat(
												e.target.value,
											);
											if (isNaN(value))
												value = gradeMedium;

											onRangeChangedHigh(
												Math.round(value * 100) / 100,
											);
										}}
										inputProps={{
											step: '0.01',
											min: 0,
											max: gradeMax,
										}}
										variant="outlined"
										type="number"
										size="small"
									/>
								);
							case 4:
								return (
									<TextField
										disabled={isLoading}
										style={{ width: '100px' }}
										value={gradeExtreme}
										onChange={e => {
											let value = parseFloat(
												e.target.value,
											);
											if (!isNaN(value)) {
												if (value < 0) value = 0;
												else if (value > gradeMax)
													value = gradeMax;

												value =
													Math.round(value * 100) /
													100;
											}

											setGradeExtreme(value);
											setIsSetupDirty(true);
										}}
										onBlur={e => {
											let value = parseFloat(
												e.target.value,
											);
											if (isNaN(value)) value = gradeHigh;

											onRangeChangedExtreme(
												Math.round(value * 100) / 100,
											);
										}}
										inputProps={{
											step: '0.01',
											min: 0,
											max: gradeMax,
										}}
										variant="outlined"
										type="number"
										size="small"
									/>
								);
						}
					}
					break;
				default:
					return cell;
			}
		});
	};

	const onIgnoreWeightings = (event: React.ChangeEvent<HTMLInputElement>) => {
		setIgnoreWeightings(event.target.checked);

		if (event.target.checked) {
			setIgnorePreviousValue(lofValue);

			const lValue = 100;
			const lScore = getScore(lValue, true);
			setLoFValue(lValue);
			setLofScore(lScore);

			const cValue = 100;
			const cScore = getScore(cValue, false);
			setCoFValue(cValue);
			setCofScore(cScore);

			maxScoreChanged(riskMethod, lScore, lValue, cScore, cValue);
		} else {
			const lValue = ignorePreviousValue;
			const lScore = getScore(lValue, true);
			setLoFValue(lValue);
			setLofScore(lScore);

			const cValue = 100 - lValue;
			const cScore = getScore(cValue, false);
			setCoFValue(cValue);
			setCofScore(cScore);

			maxScoreChanged(riskMethod, lScore, lValue, cScore, cValue);
		}

		setIsSetupDirty(true);
	};

	const titleArea = () => {
		return (
			<>
				<RadioGroup
					row
					value={riskMethod}
					onChange={(
						event: React.ChangeEvent<HTMLInputElement>,
						value: string,
					) => {
						maxScoreChanged(
							value,
							lofScore,
							lofValue,
							cofScore,
							cofValue,
						);
					}}>
					<FormControlLabel
						disabled={isLoading}
						value="added"
						control={<Radio />}
						label={t('Added')}
					/>
					<FormControlLabel
						disabled={isLoading}
						value="multiplied"
						control={<Radio />}
						label={t('Multiplied')}
					/>
				</RadioGroup>
				<FormControlLabel
					disabled={isLoading}
					control={
						<Checkbox
							checked={ignoreWeightings}
							onChange={onIgnoreWeightings}
						/>
					}
					label={t('100% LoF / 100% CoF')}
				/>
				{riskMatrixFlag && (
					<RiskModeDropdown
						setShowMethodSwitchDialog={setShowMethodSwitchDialog}
					/>
				)}
			</>
		);
	};

	const riskStore = selectRiskStore();
	const didMatrixUpdate = selectDidMatrixUpdate();

	const onSaveRiskClicked = (newRiskType: 'linear' | 'matrix') => {
		dispatch(setRiskType(newRiskType));
		if (didMatrixUpdate) {
			const oldRiskConfig = { ...riskStore.riskConfig };
			const oldRiskSetting = { ...riskStore.riskConfig.riskSetting };
			const newMatrix = {
				grades: { ...riskStore.riskMatrix.grades },
				grid: [...riskStore.riskMatrix.grid],
			};

			const newRiskTypeStr = newRiskType
				? newRiskType
				: oldRiskSetting.riskType;

			const newSetting = {
				...oldRiskSetting,
				matrix: newMatrix,
				riskType: newRiskTypeStr,
			};

			const newRiskConfig: RiskConfig = {
				...oldRiskConfig,
				riskSetting: newSetting,
			};
			dispatch(saveRiskConfigAction(newRiskConfig));
		}
		dispatch(setOnlyGradesUpdated(false));
		dispatch(setDidMatrixUpdate(false));
		dispatch(setRiskSetupDirty(false));
		dispatch(setMatrixPreviewStatus(''));
		dispatch(setSaveRun(true));
		dispatch(setRefreshList(true));
	};

	const onNormalizeChanged = (
		useNormalize: boolean,
		normalizeValue: number,
	) => {
		if (useNormalize && gradeMax !== normalizeValue) {
			const range = createRangeValues(0, normalizeValue, 1, 5);
			setGradeLow(range[1]);
			setGradeMedium(range[2]);
			setGradeHigh(range[3]);
			setGradeExtreme(range[4]);
			setGradeMax(normalizeValue);
		} else if (useNormalize !== normalizeSettings.useNormalize) {
			const range = createRangeValues(0, maxScore, 1, 5);
			setGradeLow(range[1]);
			setGradeMedium(range[2]);
			setGradeHigh(range[3]);
			setGradeExtreme(range[4]);
			setGradeMax(maxScore);
		}

		setNormalizeSettings({
			useNormalize: useNormalize,
			normalizeValue: normalizeValue,
			maxRiskScore: normalizeSettings.maxRiskScore,
		});

		setIsSetupDirty(true);
	};

	return (
		<>
			<Grid container>
				<Grid item xs={12}>
					<StylovyzeTable
						className="ScrollTable"
						title={t('Overall Risk')}
						dataCy="overallRisk-table"
						headers={overallHeaders}
						rows={
							overallRows.map((row: string[], nRow: number) => ({
								key: nRow,
								cells: overallRowReactNodes(row, nRow),
							})) || []
						}
						rowHeight={'80px'}
						titleArea={titleArea()}
						footerArea={t('Max Overall Risk Score = ') + maxScore}
					/>
				</Grid>
				{riskMatrixFlag && riskType === 'matrix' ? (
					<Grid item xs={12} style={{ paddingTop: '20px' }}>
						<RiskMatrixGrid />
					</Grid>
				) : (
					<>
						{riskNormalizeFlag ? (
							<Grid item xs={12} style={{ paddingTop: '20px' }}>
								<RiskNormalize
									isLoading={isLoading}
									normalizeSettings={normalizeSettings}
									onNormalizeChanged={onNormalizeChanged}
								/>
							</Grid>
						) : (
							[]
						)}
						<Grid item xs={12} style={{ paddingTop: '20px' }}>
							<StylovyzeTable
								className="ScrollTable"
								title={t('Risk Grade')}
								dataCy="riskGrade-table"
								headers={gradeHeaders}
								rows={
									gradeRows.map(
										(row: string[], nRow: number) => ({
											key: nRow,
											cells: gradeRowReactNodes(
												row,
												nRow,
											),
										}),
									) || []
								}
							/>
						</Grid>
					</>
				)}
			</Grid>
			<StylovyzeDialog
				data-cy="change-risk-type-dialog"
				title={t('Change Risk Method')}
				open={showMethodSwitchDialog}
				cancelText={t('Cancel')}
				cancelButtonProps={{
					dataCy: 'switch-risk-type-cancel',
				}}
				onCancel={() => setShowMethodSwitchDialog(false)}
				confirmText={t('Change Risk Method')}
				confirmButtonProps={{
					dataCy: 'switch-risk-type-confirm',
				}}
				onConfirm={() => {
					const newRiskType =
						riskType === 'linear' ? 'matrix' : 'linear';
					onSaveRiskClicked(newRiskType);
					setShowMethodSwitchDialog(false);
				}}>
				<div>
					<span>
						{t('You are about to change the risk method to ')}
					</span>
					<span>
						<strong>
							{riskType === 'linear' ? 'matrix' : 'linear'}
						</strong>
					</span>
					<span>
						{t('. The changes you have made will be saved.')}
					</span>
				</div>
				<br />
				<div>{t('Are you sure you want to do this?')}</div>
			</StylovyzeDialog>
		</>
	);
};
